import {
  FC,
  Fragment,
  ReactNode,
  lazy,
  Suspense,
  LazyExoticComponent,
} from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import GuestGuard from "src/components/GuestGuard";
import AuthGuard from "src/components/AuthGuard";
import DashboardLayout from "src/components/DashboardLayout";
import PageLoading from "src/components/PageLoading";

type HOC = FC<{ children: ReactNode }>;

type AppRoute = {
  component: FC | LazyExoticComponent<FC>;
  path: string;
  guard?: HOC;
  layout?: HOC;
};

const routes: AppRoute[] = [
  {
    component: lazy(() => import("src/pages/login")),
    guard: GuestGuard,
    path: "/login",
  },

  {
    component: lazy(() => import("src/pages/orders/OrdersList")),
    guard: AuthGuard,
    path: "/orders",
    layout: DashboardLayout,
  },
  {
    component: lazy(() => import("src/pages/users/UsersList")),
    guard: AuthGuard,
    path: "/users",
    layout: DashboardLayout,
  },
  {
    component: lazy(() => import("src/pages/invoices/InvoicesList")),
    guard: AuthGuard,
    path: "/invoices",
    layout: DashboardLayout,
  },
  {
    component: lazy(() => import("src/pages/users/EditUser")),
    guard: AuthGuard,
    path: "/users/:userId",
    layout: DashboardLayout,
  },
  {
    component: lazy(() => import("src/pages/subscriptions/SubscriptionsList")),
    guard: AuthGuard,
    path: "/subscriptions",
    layout: DashboardLayout,
  },
  {
    component: lazy(() => import("src/pages/redeemRequests/redeemRequestsList")),
    guard: AuthGuard,
    path: "/redeem-requests",
    layout: DashboardLayout,
  },
  {
    component: () => <Navigate to="/orders" />,
    guard: AuthGuard,
    path: "/",
    layout: DashboardLayout,
  },
  {
    component: () => <Navigate to="/" />,
    path: "*",
  },
];

const AppRoutes: FC = () => {
  const renderRoutes = (): JSX.Element[] => {
    return routes.map((route) => {
      const {
        path,
        component: Component,
        guard: Guard = Fragment,
        layout: Layout = Fragment,
      } = route;
      return (
        <Route
          key={path}
          path={path}
          element={
            <Guard>
              <Layout>
                <Suspense fallback={<PageLoading />}>
                  <Component />
                </Suspense>
              </Layout>
            </Guard>
          }
        />
      );
    });
  };

  return <Routes>{renderRoutes()}</Routes>;
};

export default AppRoutes;
